<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="billRate-main">
    <div>
      <Menus :menu="menu" @menuClicked="menuClicked" />
    </div>

    <div class="table-box-container">
      <TableBox title="Trucking Rate" :tableData="billRateListMaster" :maxSelection="1"
        :selectedRow.sync="billRateTableBoxSelectedRow" :loading="billRateTableBoxLoading" />
    </div>

    <PopupEntry class="popup-entry-component" :show.sync="showPopupEntry" v-if="showPopupEntry" :title="popupEntryTitle"
      :updatedBy="updatedBy" :createdBy="createdBy" @save="popupEntrySaveClicked"
      :requiredError="popupEntryRequiredError" :successMessages="popupEntrySuccessMessages"
      :messages="popupEntryMessages" :errorMessages="popupEntryErrorMessages"
      @requiredClicked="popupEntryRequiredClicked" :viewMode="viewMode" :cancelButton="viewMode ? 'Close' : 'Cancel'">
      <div class="general-inputs">
        <div class="billRate-active-date">
          <Input class="billRate-input" placeholder="Bill Rate Code" :inputValue.sync="billRateInputValue"
            :isRequired="true" :isDisabled="viewModePK || systemCodeIsChecked" :errorMessage.sync="billRateErr"
            ref="billRateErr" validator="masterfilecode,20,uppercase" />
          <CheckBoxV2 v-if="!viewModePK" class="systemcode-check-box" title="Use System Code"
            :isChecked.sync="systemCodeIsChecked" @checkBoxClicked="checkBoxChanged" />
          <DateTimePicker class="valid-from" :isDisabled="viewMode" placeholder="Valid From" :value.sync="validFr"
            :isRequired="true" :errorMessage.sync="validFrErr" :showTime="false" ref="validFrErr"/>
        </div>
        <Input placeholder="Description" :inputValue.sync="description" :isDisabled="viewMode"
          :errorMessage.sync="descriptionErr" ref="descriptionErr" validator="text,100" />
        <RadioButtonSet class="radio-button-set-component" title="Rate by Category" :buttons="rateByRadioButton"
          labelKey="label" :selected.sync="selectedRateBy" :isRequired="true" :isDisabled="viewMode"
          :isHorizontal="true"/>
      </div>

      <!-- Top -->
      <div class="rate-by-area-destination" v-if="selectedRateBy.keyValue === '1'">
        <div class="area-destination-inputs">
          <div class="input-column">
            <InputQuery placeholder="From Area" :isRequired="true" :isDisabled="viewMode" :items="areaListMaster"
              :allowFreeText="false" :selectedObject.sync="selectedFromArea" itemKey="Area Code" descKey="Description"
              :errorMessage.sync="fromAreaErr" ref="fromAreaErr" />
            <InputQuery placeholder="To Area" :isRequired="true" :isDisabled="viewMode" :items="areaListMaster"
              :allowFreeText="false" :selectedObject.sync="selectedToArea" itemKey="Area Code" descKey="Description"
              :errorMessage.sync="toAreaErr" ref="toAreaErr" />
            <InputQuery placeholder="Billing Customer" :isDisabled="viewMode" :items="billCustListMaster"
              :allowFreeText="false" :selectedObject.sync="selectedBillCust" itemKey="Customer Code"
              descKey="Customer Name" />
          </div>
          <div class="input-column">
            <InputQuery placeholder="From Destination" @selected="selectedFrom" :isDisabled="viewMode"
              :items="destinationListMaster" :allowFreeText="false" :selectedObject.sync="selectedFromDestination"
              itemKey="Destination Code" descKey="Area Code" />
            <InputQuery placeholder="To Destination" @selected="selectedTo" :isDisabled="viewMode"
              :items="destinationListMaster" :allowFreeText="false" :selectedObject.sync="selectedToDestination"
              itemKey="Destination Code" descKey="Area Code" />
          </div>
        </div>

        <!-- Bottom -->
        <div class="area-destination-inputs">
          <div class="input-column">
            <Dropdown placeholder="Job Type" :items="jobTypeListMaster" itemKey="Description"
              :selectedObject.sync="selectedJobType" :isDisabled="viewMode" />
            <Input placeholder="Per KG Rate" :inputValue.sync="perKGRate" :isDisabled="viewMode"
              validator="positiveDecimal" :errorMessage.sync="perKGRateErr" ref="perKGRateErr" />
            <Input placeholder="Per M3 Rate" :inputValue.sync="perM3Rate" :isDisabled="viewMode"
              validator="positiveDecimal,14,2" :errorMessage.sync="perM3RateErr" ref="perM3RateErr" />
            <Input placeholder="Minimum Rate" :inputValue.sync="minimumRate" :isDisabled="viewMode"
              validator="positiveDecimal,14,2" :errorMessage.sync="minimumRateErr" ref="minimumRateErr" />
            <Input placeholder="Per Pallet Rate" :inputValue.sync="perPalletRate" :isDisabled="viewMode"
              validator="positiveDecimal,14,2" :errorMessage.sync="perPalletRateErr" ref="perPalletRateErr" />
            <div class="sub-area-destination-inputs">
              <div class="sub-input-column">
                <Input placeholder="Per Package Rate" :inputValue.sync="perPackageRate" :isDisabled="viewMode"
                  validator="positiveDecimal,14,2" :errorMessage.sync="perPackageRateErr" ref="perPackageRateErr" />
                <Input placeholder="Per Truck Rate" :inputValue.sync="perTruckRate" :isDisabled="viewMode"
                  validator="positiveDecimal,14,2" :errorMessage.sync="perTruckRateErr" ref="perTruckRateErr" />
                <Input placeholder="Percentage of total product value" :inputValue.sync="percentOfTtlProdVal"
                  :isDisabled="viewMode" validator="positiveDecimal,14,2" :errorMessage.sync="percentOfTtlProdValErr"
                  ref="percentOfTtlProdValErr" />
              </div>
              <div class="sub-input-column">
                <InputQuery placeholder="Package Type" :isDisabled="viewMode" :items="packageTypeListMaster"
                  :allowFreeText="false" :selectedObject.sync="selectedPackageType" itemKey="Package Code"
                  descKey="Description" />
                <InputQuery placeholder="Truck Type" :isDisabled="viewMode" :items="truckTypeListMaster"
                  :allowFreeText="false" :selectedObject.sync="selectedTruckType" itemKey="Truck Type"
                  descKey="TypeDesc" />
                <InputQuery placeholder="Product Code" :isDisabled="viewMode" :items="productCodeListMaster"
                  :allowFreeText="false" :selectedObject.sync="selectedProductCode" itemKey="Product Code"
                  descKey="Description" />
              </div>
            </div>
            <Input placeholder="Per Journey Rate" :inputValue.sync="perJourneyRate" :isDisabled="viewMode" validator="positiveDecimal,14,2"/>
            <div class="sub-area-destination-inputs">
              <Input placeholder="Droppoint Rate" :inputValue.sync="droppointRate" :isDisabled="viewMode"
                validator="positiveDecimal,14,2" :errorMessage.sync="droppointRateErr" ref="droppointRateErr"
                style="margin-right: 5px" />
              <Input placeholder="If No. of drops exceeds" :inputValue.sync="ifNoOfDropsMoreThan" :isDisabled="viewMode"
                validator="positiveInteger,9" :errorMessage.sync="ifNoOfDropsMoreThanErr" ref="ifNoOfDropsMoreThanErr" />
            </div>
            <InputQuery placeholder="Currency" :isDisabled="viewMode" :items="currencyListMaster" :allowFreeText="false"
              :selectedObject.sync="selectedCurrency" itemKey="Currency Code" descKey="Description" />
            <Input placeholder="Currency Rate" :inputValue.sync="currencyRate" :isDisabled="viewMode"
              validator="positiveDecimal,14,4" :errorMessage.sync="currencyRateErr" ref="currencyRateErr" />
          </div>

          <!-- Right columns -->
          <div class="input-column">
            <Input placeholder="Base Amount" :inputValue.sync="baseAmount" :isDisabled="viewMode"
              validator="positiveDecimal,14,2" :errorMessage.sync="baseAmountErr" ref="baseAmountErr" />
            <!-- Sub tab -->
            <BoxContainer title="Std Toll & Drv Rate" class="drv-rate">
              <Input placeholder="Estimated KM" :inputValue.sync="estimatedKM" :isDisabled="viewMode"
                validator="positiveDecimal,15,2" :errorMessage.sync="estimatedKMErr" ref="estimatedKMErr" />
              <Input placeholder="Toll" :inputValue.sync="stdToll" :isDisabled="viewMode" validator="positiveDecimal,14,2"
                :errorMessage.sync="stdTollErr" ref="stdTollErr" />
              <BoxContainer title="Driver Hire Rate" class="sub-tab-rate-boxcontainer">
                <div class="sub-tab-rate-inputs">
                  <div class="sub-tab-rate-input-column">
                    <Input placeholder="Per KG Rate" :inputValue.sync="drvPerKGRate" :isDisabled="viewMode"
                      validator="positiveDecimal,14,4" :errorMessage.sync="drvPerKGRateErr" ref="drvPerKGRateErr" />
                    <Input placeholder="Per M3 Rate" :inputValue.sync="drvPerM3Rate" :isDisabled="viewMode"
                      validator="positiveDecimal,14,2" :errorMessage.sync="drvPerM3RateErr" ref="drvPerM3RateErr" />
                    <Input placeholder="Minimum Rate" :inputValue.sync="drvMinimumRate" :isDisabled="viewMode"
                      validator="positiveDecimal,14,2" :errorMessage.sync="drvMinimumRateErr" ref="drvMinimumRateErr" />
                  </div>
                  <div class="sub-tab-rate-input-column">
                    <Dropdown placeholder="KG Rate Type" :items="rateTypeDropdown" itemKey="label"
                      :selectedObject.sync="selectedDrvPerKGRateType" :isDisabled="viewMode" />
                    <Dropdown placeholder="M3 Rate Type" :items="rateTypeDropdown" itemKey="label"
                      :selectedObject.sync="selectedDrvPerM3RateType" :isDisabled="viewMode" />
                  </div>
                </div>
              </BoxContainer>
              <BoxContainer title="Attendant Hire Rate" class="sub-tab-rate-boxcontainer">
                <div class="sub-tab-rate-inputs">
                  <div class="sub-tab-rate-input-column">
                    <Input placeholder="Per KG Rate" :inputValue.sync="attPerKGRate" :isDisabled="viewMode"
                      validator="positiveDecimal,14,4" :errorMessage.sync="attPerKGRateErr" ref="attPerKGRateErr" />
                    <Input placeholder="Per M3 Rate" :inputValue.sync="attPerM3Rate" :isDisabled="viewMode"
                      validator="positiveDecimal,14,2" :errorMessage.sync="attPerM3RateErr" ref="attPerM3RateErr" />
                    <Input placeholder="Minimum Rate" :inputValue.sync="attMinimumRate" :isDisabled="viewMode"
                      validator="positiveDecimal,14,2" :errorMessage.sync="attMinimumRateErr" ref="attMinimumRateErr" />
                  </div>
                  <div class="sub-tab-rate-input-column">
                    <Dropdown placeholder="KG Rate Type" :items="rateTypeDropdown" itemKey="label"
                      :selectedObject.sync="selectedAttPerKGRateType" :isDisabled="viewMode" />
                    <Dropdown placeholder="M3 Rate Type" :items="rateTypeDropdown" itemKey="label"
                      :selectedObject.sync="selectedAttPerM3RateType" :isDisabled="viewMode" />
                  </div>
                </div>
              </BoxContainer>
            </BoxContainer>
          </div>
        </div>

        <!-- Toll Rates -->
        <div class="area-destination-inputs">
          <div class="toll-rates-list">
            <div class="toll-rates-table">
              <div v-if="!viewMode" class="call-to-action-buttons">
                <button @click="addTollRatesClicked" :disabled="showTollRatesInputs">Add</button>
                <button @click="deleteTollRatesClicked" :disabled="showTollRatesInputs">Delete</button>
              </div>

              <div class="table-box-container">
                <TableBox v-if="showTollRatesTableBox" title="Toll Rates" :tableData="tollRatesTableDataWithoutDeleted"
                  :selectedRow.sync="tollRatesTableDataSelectedRow" :hideColumns="tollRateTableBoxHideColumns" />
              </div>
            </div>

            <div v-if="showTollRatesInputs" class="toll-rates-inputs">
              <div class="call-to-action-buttons">
                <button v-if="tollRatesBeingEdited === null" @click="addTollRatesInputClicked"
                  style="margin-right: 5px;">Add</button>
                <button @click="cancelTollRatesClicked">Cancel</button>
              </div>
              <div style="margin-top: 10px;">
                <InputQuery placeholder="Truck Type" :isRequired="true" :isDisabled="viewMode"
                  :items="truckTypeListMaster" :allowFreeText="false" :selectedObject.sync="selectedTollTruckType"
                  itemKey="Truck Type" :showDesc="false" :errorMessage.sync="tollTruckTypeErr" ref="tollTruckTypeErr" />
                <div style="margin-right: 20px;">
                  <Input :isDisabled="viewMode" placeholder="Toll Rate" :inputValue.sync="tollRates"
                    validator="positiveDecimal,14,2" :errorMessage.sync="tollRatesErr" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Rate by Zone, by Product -->
      <div class="rate-by-zone-product" v-if="selectedRateBy.keyValue === '2'">
        <div class="zone-product-inputs">
          <InputQuery placeholder="From Zone" :isRequired="true" :isDisabled="viewMode" :items="zoneListMaster"
            :allowFreeText="false" :selectedObject.sync="selectedFromZone" itemKey="Zone Type" descKey="Description"
            :errorMessage.sync="fromZoneErr" ref="fromZoneErr" />
          <InputQuery placeholder="To Zone" :isRequired="true" :isDisabled="viewMode" :items="zoneListMaster"
            :allowFreeText="false" :selectedObject.sync="selectedToZone" itemKey="Zone Type" descKey="Description"
            :errorMessage.sync="toZoneErr" ref="toZoneErr" />
          <InputQuery placeholder="Item Type" :isRequired="true" :isDisabled="viewMode" :items="itemTypeListMaster"
            :allowFreeText="false" :selectedObject.sync="selectedItemType" itemKey="Item Type" descKey="Description"
            :errorMessage.sync="itemTypeErr" ref="itemTypeErr" />
          <InputQuery placeholder="Billing Customer" :isDisabled="viewMode" :items="billCustListMaster"
            :allowFreeText="false" :selectedObject.sync="selectedBillCust" itemKey="Customer Code"
            descKey="Customer Name" />
          <div class="input-row">
            <Input placeholder="From" :inputValue.sync="from1" :isDisabled=true />
            <Input placeholder="To" :inputValue.sync="to1" :isDisabled="viewMode" validator="positiveDecimal,14,2"
              :errorMessage.sync="to1Err" ref="to1Err" @focusChanged="toFocusChanged"/>
            <div>=</div>
            <Input placeholder="Rate" :inputValue.sync="rate1" :isDisabled="viewMode" validator="positiveDecimal,14,6"
              :errorMessage.sync="rate1Err" ref="rate1Err" />
          </div>
          <div class="input-row">
            <Input placeholder="From" :inputValue.sync="from2" :isDisabled=true />
            <Input placeholder="To" :inputValue.sync="to2" :isDisabled="viewMode" validator="positiveDecimal,14,2"
              :errorMessage.sync="to2Err" ref="to2Err" @focusChanged="toFocusChanged" />
            <div>=</div>
            <Input placeholder="Rate" :inputValue.sync="rate2" :isDisabled="viewMode" validator="positiveDecimal,14,6"
              :errorMessage.sync="rate2Err" ref="rate2Err" />
          </div>
          <div class="input-row">
            <Input placeholder="From" :inputValue.sync="from3" :isDisabled=true />
            <Input placeholder="To" :inputValue.sync="to3" :isDisabled="viewMode" validator="positiveDecimal,14,2"
              :errorMessage.sync="to3Err" ref="to3Err" @focusChanged="toFocusChanged" />
            <div>=</div>
            <Input placeholder="Rate" :inputValue.sync="rate3" :isDisabled="viewMode" validator="positiveDecimal,14,6"
              :errorMessage.sync="rate3Err" ref="rate3Err" />
          </div>
          <div class="input-row">
            <Input placeholder="From" :inputValue.sync="from4" :isDisabled=true />
            <Input placeholder="To" :inputValue.sync="to4" :isDisabled="viewMode" validator="positiveDecimal,14,2"
              :errorMessage.sync="to4Err" ref="to4Err" @focusChanged="toFocusChanged" />
            <div>=</div>
            <Input placeholder="Rate" :inputValue.sync="rate4" :isDisabled="viewMode" validator="positiveDecimal,14,6"
              :errorMessage.sync="rate4Err" ref="rate4Err" />
          </div>
        </div>
        <Input placeholder="Minimum Rate" :inputValue.sync="minimumRate" :isDisabled="viewMode"
          validator="positiveDecimal,14,2" :errorMessage.sync="minimumRateErr" ref="minimumRateErr" />
      </div>
    </PopupEntry>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  components: {
  },
  computed: {
    tollRatesTableDataWithoutDeleted: function () {
      return this.tollRatesTableData.filter((tollRatesData) => tollRatesData._action !== "D");
    },
  },
  data: function () {
    return {
      // Used to store all responses from GetListMaster, some of them may be shared between multiple Dropdown or InputQuery
      billRateListMaster: [],
      areaListMaster: [],
      destinationListMaster: [],
      billCustListMaster: [],
      jobTypeListMaster: [],
      packageTypeListMaster: [],
      truckTypeListMaster: [],
      productCodeListMaster: [],
      currencyListMaster: [],
      zoneListMaster: [],
      itemTypeListMaster: [],
      rateTypeDropdown: [
        { "label": "$" },
        { "label": "%" },
      ],
      rateByRadioButton: [
        { label: "Rate by Area / Destination", keyValue: '1' },
        { label: "Rate by Zone, by Product", keyValue: '2' },
      ],

      menu: [
        { title: 'New Trucking Rate', icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png', key: 'MnuNew', visible: true, enabled: true },
        { title: 'Edit Trucking Rate', icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png', key: 'MnuEdit', visible: true, enabled: true },
        { title: 'Delete Trucking Rate', icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png', key: 'MnuDelete', visible: true, enabled: true },
        { title: 'View Trucking Rate', icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png', key: 'MnuQuery', visible: true, enabled: true },
      ],


      billRateTableBoxSelectedRow: [],
      billRateTableBoxLoading: false,

      //popup entry state
      showPopupEntry: false,
      updatedBy: "",
      createdBy: "",
      viewMode: false,
      viewModePK: false,
      popupEntryRequiredError: [],
      popupEntrySuccessMessages: [],
      popupEntryMessages: [],
      popupEntryErrorMessages: [],

      showTollRatesInputs: false,
      showTollRatesTableBox: true,

      //popup entry common fields
      billRateInputValue: '',
      billRateErr: '',
      systemCodeIsChecked: false,
      validFr: null,
      validFrErr: '',
      description: '',
      descriptionErr: '',

      // rate by area / destination fields
      perKGRate: '0.0000',
      perKGRateErr: '',
      perM3Rate: '0.00',
      perM3RateErr: '',
      minimumRate: '0.00',
      minimumRateErr: '',
      perPalletRate: '0.00',
      perPalletRateErr: '',
      perPackageRate: '0.00',
      perPackageRateErr: '',
      perTruckRate: '0.00',
      perTruckRateErr: '',
      percentOfTtlProdVal: '0.00',
      percentOfTtlProdValErr: '',
      perJourneyRate: '0.00',
      perJourneyRateErr: '',
      droppointRate: '0.00',
      droppointRateErr: '',
      ifNoOfDropsMoreThan: '0',
      ifNoOfDropsMoreThanErr: '',
      currencyRate: '1.0000',
      currencyRateErr: '',
      baseAmount: '0.00',
      baseAmountErr: '',
      estimatedKM: '0.00',
      estimatedKMErr: '',
      stdToll: '0.00',
      stdTollErr: '',
      drvPerKGRate: '0.0000',
      drvPerKGRateErr: '',
      drvPerM3Rate: '0.00',
      drvPerM3RateErr: '',
      drvMinimumRate: '0.00',
      drvMinimumRateErr: '',
      attPerKGRate: '0.0000',
      attPerKGRateErr: '',
      attPerM3Rate: '0.00',
      attPerM3RateErr: '',
      attMinimumRate: '0.00',
      attMinimumRateErr: '',

      // truck type
      tollRatesTableData: [],
      tollRatesTableDataSelectedRow: [],
      tollRateTableBoxHideColumns: ["Bill Rate Code", "_action"],
      tollRatesBeingEdited: null,
      tollRates: '0.00',
      tollRatesErr: '',

      // rate by zone product fields
      from1: '0.00',
      to1: '0.00',
      to1Err: '',
      rate1: '0.000000',
      rate1Err: '',
      from2: '0.00',
      to2: '0.00',
      to2Err: '',
      rate2: '0.000000',
      rate2Err: '',
      from3: '0.00',
      to3: '0.00',
      to3Err: '',
      rate3: '0.000000',
      rate3Err: '',
      from4: '0.00',
      to4: '0.00',
      to4Err: '',
      rate4: '0.000000',
      rate4Err: '',

      // inputqueries 
      selectedFromArea: null,
      fromAreaErr: '',
      selectedToArea: null,
      toAreaErr: '',
      selectedFromDestination: null,
      selectedToDestination: null,
      selectedBillCust: null,
      selectedJobType: null,
      selectedPackageType: null,
      selectedTruckType: null,
      selectedProductCode: null,
      selectedCurrency: null,
      selectedFromZone: null,
      fromZoneErr: '',
      selectedToZone: null,
      toZoneErr: '',
      selectedItemType: null,
      itemTypeErr: '',
      selectedRateBy: 1,
      selectedDrvPerKGRateType: null,
      selectedDrvPerM3RateType: null,
      selectedAttPerKGRateType: null,
      selectedAttPerM3RateType: null,
      selectedTollTruckType: null,
      tollTruckTypeErr: '',
    };
  },
  props: {
  },
  methods: {
    getBillRateListMaster: async function () {
      this.billRateTableBoxLoading = true;
      this.billRateListMaster = [];
      this.billRateTableBoxSelectedRow = [];
      this.billRateListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'BILLRATE' });
      this.billRateTableBoxLoading = false;
    },
    getAreaListMasterListMaster: async function () {
      this.areaListMaster = [];
      this.areaListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'AREA' });
    },
    getDestinationListMaster: async function () {
      this.destinationListMaster = [];
      this.destinationListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'DESTINATION' });
    },
    getBillCustListMaster: async function () {
      this.billCustListMaster = [];
      this.billCustListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CUSTOMER' });
    },
    getJobTypeListMaster: async function () {
      this.jobTypeListMaster = [];
      this.jobTypeListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'JOBTYPE', module: 'TMS' });
    },
    getPackageListMaster: async function () {
      this.packageTypeListMaster = [];
      this.packageTypeListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'WHPACKAGE' });
    },
    getTruckTypeListMaster: async function () {
      this.truckTypeListMaster = [];
      this.truckTypeListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'TRUCKTYPE' });
    },
    getProductCodeListMaster: async function () {
      this.productCodeListMaster = [];
      this.productCodeListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'WHPRODUCT' });
    },
    getCurrencyListMaster: async function () {
      this.currencyListMaster = [];
      this.currencyListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CURRENCY' });
    },
    getZoneListMaster: async function () {
      this.zoneListMaster = [];
      this.zoneListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'ZONE' });
    },
    getItemTypeListMaster: async function () {
      this.itemTypeListMaster = [];
      this.itemTypeListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'ITEMTYPE' });
    },
    getBillRateDetails: async function (billRate, validFr) {
      return (await this.$apiCallV2('/Master/GetDetailsMaster', { "masterfile": "BILLRATE", "module": "MASTER", "recordid": billRate, "startdate": validFr }))
    },
    menuClicked: async function (e) {
      this.clearPopupEntryMessages();
      this.resetPopupEntryFields();

      switch (e.title) {
        case 'New Trucking Rate':
          this.popupEntryTitle = 'New Trucking Rate';
          this.viewMode = false;
          this.viewModePK = false;
          this.showPopupEntry = true;
          break;

        case "Edit Trucking Rate":
          if (this.billRateTableBoxSelectedRow == 0) {
            this.$showAlert('error', 'No Trucking Rate is selected', 'Please select a Trucking Rate to edit');
            break;
          }

          this.$fullscreenLoading("Getting Trucking Rate Details");
          this.populatePopupEntryFields(
            (await this.getBillRateDetails(this.billRateTableBoxSelectedRow[0]["Bill Rate Code"], this.billRateTableBoxSelectedRow[0]['Valid From'])).BillRate
          );
          this.$fullscreenLoading();

          this.viewMode = false;
          this.viewModePK = true;
          this.showPopupEntry = true;
          this.popupEntryTitle = "Edit Trucking Rate";
          break;

        case "Delete Trucking Rate":
          if (this.billRateTableBoxSelectedRow == 0) {
            this.$showAlert('error', 'No Trucking Rate is selected', 'Please select a Trucking Rate to delete');
            break;
          }

          this.$fullscreenLoading("Getting Trucking Rate Details");
          this.populatePopupEntryFields(
            (await this.getBillRateDetails(this.billRateTableBoxSelectedRow[0]["Bill Rate Code"], this.billRateTableBoxSelectedRow[0]['Valid From'])).BillRate
          );
          this.$fullscreenLoading();

          let promptMessage = `Are you sure you want to delete Trucking Rate <b>${this.billRateTableBoxSelectedRow[0]["Bill Rate Code"]}</b>?`;
          let additionalDeletion = [];
          if (this.tollRatesTableData.length > 0) {
            additionalDeletion.push(`<b>${this.tollRatesTableData.length} toll rates${this.tollRatesTableData.length > 1 ? "s" : ""}</b`);
          }
          if (additionalDeletion.length > 0) {
            promptMessage += `<br><br>This will also delete ${additionalDeletion.join(" and ")}`;
          }

          const deleteConfirmation = await this.$showAlert('warning', 'Delete Trucking Rate', promptMessage, [
            { text: 'Delete', textColor: 'white', color: 'red' },
            { text: 'Cancel' },
          ]);
          if (deleteConfirmation == 'Cancel') {
            break;
          }

          this.$fullscreenLoading("Deleting Trucking Rate");
          const result = await this.$apiCallV2('/BillRate/SaveBillRate', this.formatSaveBillRateRequestBody("D"));
          this.$fullscreenLoading();

          if (result[0]['Results'] != '0') {
            this.$showAlert('error', 'Trucking Rate NOT Deleted', `There was an error deleting Truckinmg Rate ${this.billRateTableBoxSelectedRow[0]['Bill Rate Code']}`);
            break;
          }

          // Delete successful
          this.$showAlert('success', 'Trucking Rate Deleted', `Successfully deleted Trucking Rate ${this.billRateTableBoxSelectedRow[0]['Bill Rate Code']}`);
          this.billRateTableBoxSelectedRow = [];
          this.getBillRateListMaster();
          break;

        case 'View Trucking Rate':
          if (this.billRateTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No Trucking Rate is selected', 'Please select a Trucking Rate to view');
            break;
          }

          this.$fullscreenLoading("Getting Trucking Rate Details");
          this.populatePopupEntryFields(
            (await this.getBillRateDetails(this.billRateTableBoxSelectedRow[0]['Bill Rate Code'], this.billRateTableBoxSelectedRow[0]['Valid From'])).BillRate
          );
          this.$fullscreenLoading();

          this.popupEntryTitle = "View Bill Rate";
          this.viewMode = true;
          this.viewModePK = true;
          this.showPopupEntry = true;
          break;
      }
    },
    popupEntryRequiredClicked: function (item) {
      this.$nextTick(() => {
        this.$refs[item].setFocus();
      });
    },
    clearPopupEntryMessages: function () {
      this.popupEntryRequiredError = [];
      this.popupEntrySuccessMessages = [];
      this.popupEntryMessages = [];
      this.popupEntryErrorMessages = [];
    },
    resetPopupEntryFields: function () { // clears off most fields, and set defaults for some
      // popup entry state
      this.popupEntryTitle = "";
      this.createdBy = '';
      this.updatedBy = '';

      //common fields
      this.billRateInputValue = '';
      this.billRateErr = '';
      this.systemCodeIsChecked = false;
      this.validFr = null;
      this.validFrErr = '';
      this.description = '';
      this.selectedRateBy = this.populateObject(this.rateByRadioButton, 'label', 'Rate by Area / Destination');

      // rate by area / destination fields
      this.perKGRate = '0.0000';
      this.perKGRateErr = '';
      this.perM3Rate = '0.00';
      this.perM3RateErr = '';
      this.minimumRate = '0.00';
      this.minimumRateErr = '';
      this.perPalletRate = '0.00';
      this.perPalletRateErr = '';
      this.perTruckRate = '0.00';
      this.perTruckRateErr = '';
      this.perPackageRate = '0.00';
      this.perPackageRateErr = '';
      this.percentOfTtlProdVal = '0.00';
      this.percentOfTtlProdValErr = '';
      this.perJourneyRate = '0.00';
      this.perJourneyRateErr = '';
      this.droppointRate = '0.00';
      this.droppointRateErr = '';
      this.ifNoOfDropsMoreThan = '0';
      this.ifNoOfDropsMoreThanErr = '';
      this.currencyRate = '1.0000';
      this.currencyRateErr = '';
      this.baseAmount = '0.00';
      this.baseAmountErr = '';
      this.estimatedKM = '0.00';
      this.estimatedKMErr = '';
      this.stdToll = '0.00';
      this.stdTollErr = '';
      this.drvPerKGRate = '0.0000';
      this.drvPerKGRateErr = '';
      this.drvPerM3Rate = '0.00';
      this.drvPerM3RateErr = '';
      this.drvMinimumRate = '0.00';
      this.drvMinimumRateErr = '';
      this.attPerKGRate = '0.0000';
      this.attPerKGRateErr = '';
      this.attPerM3Rate = '0.00';
      this.attPerM3RateErr = '';
      this.attMinimumRate = '0.00';
      this.attMinimumRateErr = '';
      this.tollRates = '0.00';
      this.tollRatesErr = '';
      this.tollRatesTableData = [];
      this.showTollRatesInputs = false;

      this.from1 = '0.00';
      this.to1 = '0.00';
      this.rate1 = '0.000000';
      this.from2 = '0.00';
      this.to2 = '0.00';
      this.rate2 = '0.000000';
      this.from3 = '0.00';
      this.to3 = '0.00';
      this.rate3 = '0.000000';
      this.from4 = '0.00';
      this.to4 = '0.00';
      this.rate4 = '0.000000';

      this.selectedFromArea = null;
      this.fromAreaErr = '';
      this.selectedToArea = null;
      this.toAreaErr = '';
      this.selectedFromDestination = null;
      this.selectedToDestination = null;
      this.selectedPackageType = null;
      this.selectedTruckType = null;
      this.selectedProductCode = null;
      this.selectedCurrency = null;
      this.selectedDrvPerKGRateType = null;
      this.selectedFromZone = null;
      this.fromZoneErr = '';
      this.selectedToZone = null;
      this.toZoneErr = '';
      this.selectedItemType = null;
      this.itemTypeErr = '';
      this.selectedDrvPerKGRateType = null;
      this.selectedDrvPerM3RateType = null;
      this.selectedAttPerKGRateType = null;
      this.selectedAttPerM3RateType = null;
      this.selectedTollTruckType = null;
      this.tollTruckTypeErr = null;
      this.selectedBillCust = null;
      this.selectedJobType = null;
    },
    populatePopupEntryFields: function (billRateDetails) {
      // popup entry state
      this.createdBy = `${billRateDetails['User Created']?.trim() ?? ''} ${billRateDetails['Created On'] ? new Date(billRateDetails['Created On']).formatString("dd/MM/yyyy HH:mm") : ''}`;
      this.updatedBy = `${billRateDetails['User ID']?.trim() ?? ''} ${billRateDetails['Date Updated'] ? new Date(billRateDetails['Date Updated']).formatString("dd/MM/yyyy HH:mm") : ''}`;

      // common fields
      this.billRateInputValue = this.populateTextTrim(billRateDetails['Bill Rate Code']);
      this.systemCodeIsChecked = billRateDetails["System Code"] == "1";
      this.validFr = new Date(billRateDetails['Valid From']);
      this.description = this.populateTextTrim(billRateDetails["Description"]);
      this.selectedRateBy = billRateDetails['Rate By Category'] ? this.populateObject(this.rateByRadioButton, 'keyValue', billRateDetails['Rate By Category']) : null;

      this.selectedFromArea = billRateDetails['From Area'] ? this.populateObject(this.areaListMaster, 'Area Code', billRateDetails['From Area']) : null;
      this.selectedToArea = billRateDetails['To Area'] ? this.populateObject(this.areaListMaster, 'Area Code', billRateDetails['To Area']) : null;
      this.selectedFromDestination = billRateDetails['From Destination'] ? this.populateObject(this.destinationListMaster, 'Destination Code', billRateDetails['From Destination']) : null;
      this.selectedToDestination = billRateDetails['To Destination'] ? this.populateObject(this.destinationListMaster, 'Destination Code', billRateDetails['To Destination']) : null;
      this.selectedBillCust = billRateDetails['Billing Customer'] ? this.populateObject(this.billCustListMaster, 'Customer Code', billRateDetails['Billing Customer']) : null;
      this.selectedJobType = billRateDetails['Job Type'] ? this.populateObject(this.jobTypeListMaster, 'Job Type', billRateDetails['Job Type']) : null;
      this.perKGRate = this.populateTextTrim(billRateDetails['Per KG Rate']);
      this.perM3Rate = this.populateTextTrim(billRateDetails['Per M3 Rate']);
      this.minimumRate = this.populateTextTrim(billRateDetails['Minimum Rate']);
      this.perPalletRate = this.populateTextTrim(billRateDetails['Per Pallet Rate']);
      this.perPackageRate = this.populateTextTrim(billRateDetails['Per Package Rate']);
      this.selectedPackageType = billRateDetails['Package Type'] ? this.populateObject(this.packageTypeListMaster, 'Package Code', billRateDetails['Package Type']) : null;
      this.perTruckRate = this.populateTextTrim(billRateDetails['Per Truck Rate']);
      this.selectedTruckType = billRateDetails['Truck Type'] ? this.populateObject(this.truckTypeListMaster, 'Truck Type', billRateDetails['Truck Type']) : null;
      this.percentOfTtlProdVal = this.populateTextTrim(billRateDetails['Percentage of total product value']);
      this.selectedProductCode = billRateDetails['Product Code'] ? this.populateObject(this.productCodeListMaster, 'Product Code', billRateDetails['Product Code']) : null;
      this.perJourneyRate = this.populateTextTrim(billRateDetails['Per Journey Rate']);
      this.droppointRate = this.populateTextTrim(billRateDetails['Droppoint Rate']);
      this.ifNoOfDropsMoreThan = this.populateTextTrim(billRateDetails['if # of drops >']);
      this.selectedCurrency = billRateDetails['Currency'] ? this.populateObject(this.currencyListMaster, 'Currency Code', billRateDetails['Currency']) : null;
      this.currencyRate = this.populateTextTrim(billRateDetails['Currency Rate']);
      this.baseAmount = this.populateTextTrim(billRateDetails['Base Amount']);
      this.estimatedKM = this.populateTextTrim(billRateDetails['Estimated KM']);
      this.stdToll = this.populateTextTrim(billRateDetails['Toll']);
      this.drvPerKGRate = this.populateTextTrim(billRateDetails['Driver Per KG Rate']);
      this.selectedDrvPerKGRateType = billRateDetails['Driver Tonne Rate Type'] ? this.populateObject(this.rateTypeDropdown, 'label', billRateDetails['Driver Tonne Rate Type']) : null;
      this.drvPerM3Rate = this.populateTextTrim(billRateDetails['Driver Per M3 Rate']);
      this.selectedDrvPerM3RateType = billRateDetails['Driver M3 Rate Type'] ? this.populateObject(this.rateTypeDropdown, 'label', billRateDetails['Driver M3 Rate Type']) : null;
      this.drvMinimumRate = this.populateTextTrim(billRateDetails['Driver Minimum Rate']);
      this.attPerKGRate = this.populateTextTrim(billRateDetails['Attendant Per KG Rate']);
      this.selectedAttPerKGRateType = billRateDetails['Attendant Tonne Rate Type'] ? this.populateObject(this.rateTypeDropdown, 'label', billRateDetails['Attendant Tonne Rate Type']) : null;
      this.attPerM3Rate = this.populateTextTrim(billRateDetails['Attendant Per M3 Rate']);
      this.selectedAttPerM3RateType = billRateDetails['Attendant M3 Rate Type'] ? this.populateObject(this.rateTypeDropdown, 'label', billRateDetails['Attendant M3 Rate Type']) : null;
      this.attMinimumRate = this.populateTextTrim(billRateDetails['Attendant Minimum Rate']);

      this.selectedFromZone = billRateDetails['From Zone'] ? this.populateObject(this.zoneListMaster, 'Zone Type', billRateDetails['From Zone']) : null;
      this.selectedToZone = billRateDetails['To Zone'] ? this.populateObject(this.zoneListMaster, 'Zone Type', billRateDetails['To Zone']) : null;
      this.selectedItemType = billRateDetails['Item Type'] ? this.populateObject(this.itemTypeListMaster, 'ItemType', billRateDetails['Item Type']) : null;
      this.to1 = this.populateTextTrim(billRateDetails['Range 1']);
      this.rate1 = this.populateTextTrim(billRateDetails['Range 1 Rate']);
      if(this.rate1 != "0.00"){
        this.from2 = (parseFloat(billRateDetails['Range 1']) + 1).toFixed(2);
      }      
      this.to2 = this.populateTextTrim(billRateDetails['Range 2']);
      this.rate2 = this.populateTextTrim(billRateDetails['Range 2 Rate']);
      this.from3 = (parseFloat(billRateDetails['Range 2']) + 1).toFixed(2);
      this.to3 = this.populateTextTrim(billRateDetails['Range 3']);
      this.rate3 = this.populateTextTrim(billRateDetails['Range 3 Rate']);
      this.from4 = (parseFloat(billRateDetails['Range 3']) + 1).toFixed(2);
      this.to4 = this.populateTextTrim(billRateDetails['Range 4']);
      this.rate4 = this.populateTextTrim(billRateDetails['Range 4 Rate']);

      this.tollRatesTableData = billRateDetails['BillRateToll'] ?? [];
          },
    checkBoxChanged: function (isChecked) {
      if (isChecked) {
        this.billRateInputValue = "********";
      } else {
        this.billRateInputValue = "";
      }
      this.billRateErr = "";
    },
    addTollRatesClicked: function (pointerEvent) {
      this.clearTollRatesInputs();
      this.showTollRatesInputs = true;
    },
    deleteTollRatesClicked: async function (pointerEvent) {
      if (this.tollRatesTableDataSelectedRow.length === 0) {
        this.$showAlert("error", "No Toll Rates Selected", "Please select a toll rates to delete", [{ "text": "OK" }]);
        return;
      }

      const deleteConfirmation = await this.$showAlert(
        "warning", "Delete Toll Rates", "Are you sure you wish to delete the selected toll rates?",
        [{ "text": "Delete", "color": "Red", "textColor": "white" }, { "text": "Cancel" }]
      );
      if (deleteConfirmation === "Cancel") {
        return;
      }

      const tollRatesToDelete = this.tollRatesTableDataSelectedRow[0];
      let matchedIndex;
      if (matchedIndex === undefined && tollRatesToDelete._rowid) {
        const foundIndex = this.tollRatesTableData.findIndex((tollRatesData) => tollRatesData._rowid === tollRatesToDelete._rowid);
        if (foundIndex !== -1) {
          matchedIndex = foundIndex;
        }
      }

      if (matchedIndex === undefined) {
        console.error("Toll Rates - Could not find the row being deleted");
        return;
      }

      this.tollRatesTableData.forEach((item) => {
        if (item['#'] > this.tollRatesTableData[matchedIndex]['#']) {
          item['#'] = (parseInt(item['#']) - 1).toString();
        }
      });

      if (tollRatesToDelete._action === "N") {
        this.tollRatesTableData.splice(matchedIndex, 1);
      } else {
        this.tollRatesTableData[matchedIndex]._action = "D";
      }

      const modifiedTollRatesTableData = this.tollRatesTableData.map((tollRatesData) => {
        delete tollRatesData._rowid;
        return tollRatesData;
      });
      this.showTollRatesTableBox = false;
      this.tollRatesTableData = [];
      this.tollRatesTableDataSelectedRow = [];
      this.$nextTick(() => {
        this.tollRatesTableData = modifiedTollRatesTableData;
        this.showTollRatesTableBox = true;
      });
    },
    cancelTollRatesClicked: function (pointerEvent) {
      this.tollRatesBeingEdited = null;
      this.showTollRatesInputs = false;
    },
    clearTollRatesInputs: function () {
      this.selectedTollTruckType = null;
      this.tollTruckTypeErr = '';
      this.tollRates = '0.00';
    },
    populateTollRatesInput: function (billRateDetails) {
      this.selectedTollTruckType = this.populateObject(this.truckTypeListMaster, "Truck Type", billRateDetails['T.Truck']);
      this.tollRates = this.populateTextTrim(billRateDetails['Toll Rates']);
    },
    addTollRatesInputClicked: function (pointerEvent) {
      const isInvalid = this.checkTollRatesFields();
      if (isInvalid) {
        return;
      }

      const modifiedTollRatesTableData = [
        ...this.tollRatesTableData.map((tollRatesData) => {
          delete tollRatesData._rowid;
          return tollRatesData;
        }),
        {
          "#": (this.tollRatesTableDataWithoutDeleted.length + 1).toString(),
          "Truck Type": this.selectedTollTruckType?.["Truck Type"] ?? "",
          "Toll Rate": this.tollRates,
          _action: "N"
        },
      ];
      this.showTollRatesTableBox = false;
      this.tollRatesTableData = [];
      this.tollRatesTableDataSelectedRow = [];
      this.showTollRatesInputs = false;
      this.$nextTick(() => {
        this.tollRatesTableData = modifiedTollRatesTableData;
        this.showTollRatesTableBox = true;
      });
    },
    promptDiscardBillRatesChanges: function () {
      return this.$showAlert(
        "warning", "Discard Changes",
        "You have unsaved bill rate changes.<br>Do you wish to discard the changes?",
        [{ "text": "Discard", "color": "Red", "textColor": "white" }, { "text": "Cancel" }]
      );
    },
    checkTollRatesFields: function () {
      let tollRatesArr = [];
      let error = false;

      this.clearPopupEntryMessages();
      for (const tollRatesData of this.tollRatesTableData) {
        tollRatesArr.push(tollRatesData['Truck Type']);
      }

      if (this.showTollRatesInputs) {
        if (!this.selectedTollTruckType) {
          this.tollTruckTypeErr = 'Truck Type is required!';
          this.popupEntryRequiredError.push({ key: 'tollTruckTypeErr', value: 'Truck Type is required! ' });
          error = true;
        } else if (tollRatesArr.includes(this.selectedTollTruckType['Truck Type'])) {
          this.tollTruckTypeErr = 'Truck Type is already exist!';
          this.popupEntryRequiredError.push({ key: 'tollTruckTypeErr', value: 'Truck Type is already exist! ' });
          error = true;
        }
      }
      return error;
    },
    checkPopupEntryFields: function () {
      let error = false;

      this.clearPopupEntryMessages();
      if (this.billRateInputValue.trim() === "") {
        this.billRateErr = "Bill Rate Code is required!";
        this.popupEntryRequiredError.push({ key: 'billRateErr', value: 'Bill Rate Code is required! ' });
        error = true;
      } else if (this.billRateInputValue.length > 20) {
        this.billRateErr = "Bill Rate Code cannot exceed 20 characters!";
        this.popupEntryRequiredError.push({ key: 'billRateErr', value: 'Bill Rate Code exceed 20 characters! ' });
        error = true;
      }

      if (!this.validFr) {
        this.validFrErr = "Valid From is required!";
        this.popupEntryRequiredError.push({ key: 'validFrErr', value : this.validFrErr});
        error = true;
      }

      if (this.validFr <= new Date('1900-01-01')){
        this.validFrErr = "Valid from input is invalid";
        this.popupEntryRequiredError.push({ key: 'validFrErr', value: this.validFrErr });
        error = true;
      }

      if (this.selectedRateBy.keyValue === '1') {
        if (!this.selectedFromArea) {
          this.fromAreaErr = "From Area is required!";
          this.popupEntryRequiredError.push({ key: 'fromAreaErr', value: 'From Area is required! ' });
          error = true;
        }

        if (!this.selectedToArea) {
          this.toAreaErr = "To Area is required!";
          this.popupEntryRequiredError.push({ key: 'toAreaErr', value: 'To Area is required! ' });
          error = true;
        }
      } else {
        if (!this.selectedFromZone) {
          this.fromZoneErr = "From Zone is required! ";
          this.popupEntryRequiredError.push({ key: 'fromZoneErr', value: 'From Zone is required! ' });
          error = true;
        }

        if (!this.selectedToZone) {
          this.toZoneErr = "To Zone is required! ";
          this.popupEntryRequiredError.push({ key: 'toZoneErr', value: 'To Zone is required! ' });
          error = true;
        }
        if (Number(this.to2) <= Number(this.from2)){
          this.to2Err = "To must be higher than From";
          this.popupEntryRequiredError.push({key: 'to2Err', value : this.to2Err});
          error = true;
        }
        if (Number(this.to3) <= Number(this.from3)){
          this.to3Err = "To must be higher than From";
          this.popupEntryRequiredError.push({key:'to3Err', value : this.to3Err});
          error = true;
        }

        if (Number(this.to4) <= Number(this.from4)){
          this.to4Err = "To must be higher than From";
          this.popupEntryRequiredError.push({key:'to4Err', value: this.to4Err});
          error = true;
        }

        if (this.minimumRate){
          if ( Number(this.rate1) < Number(this.minimumRate)){
            this.rate1Err = "Rate must be higher than minimum rate";
            this.popupEntryRequiredError.push({key:'rate1Err', value: this.rate1Err});
            error = true;
          }
          if ( Number(this.rate2) < Number(this.minimumRate)){
            this.rate2Err = "Rate must be higher than minimum rate";
            this.popupEntryRequiredError.push({key:'rate2Err', value: this.rate2Err});
            error = true;
          }
          if ( Number(this.rate3) < Number(this.minimumRate) ){
            this.rate3Err = "Rate must be higher than minimum rate";
            this.popupEntryRequiredError.push({key:'rate3Err', value: this.rate3Err});
            error = true;
          }
          if ( Number(this.rate4) < Number(this.minimumRate)){
            this.rate4Err = "Rate must be higher than minimum rate";
            this.popupEntryRequiredError.push({key:'rate4Err', value: this.rate4Err});
            error = true;
          }
        }


        if (!this.selectedItemType) {
          this.itemTypeErr = "Item Type is required! ";
          this.popupEntryRequiredError.push({ key: 'itemTypeErr', value: 'Item Type is required! ' });
          error = true;
        }
      }
      return error;

    },
    popupEntrySaveClicked: async function () {
      if (this.showTollRatesInputs) {
        const discardChanges = await this.promptDiscardBillRatesChanges();
        if (discardChanges === "Cancel") {
          return;
        }
        this.showTollRatesInputs = false;
        this.tollRatesBeingEdited = null;
      }

      const isInvalid = this.checkPopupEntryFields();
      if (isInvalid) {
        return;
      }

      const action = this.popupEntryTitle[0];
      const requestBody = this.formatSaveBillRateRequestBody(action);

      this.$fullscreenLoading('Saving Trucking Rate');
      const result = await this.$apiCallV2("/BillRate/SaveBillRate", requestBody);
      this.$fullscreenLoading();

      // Save Error
      if (result[0]['Results'] != "0") {
        if (action == "N") {
          this.popupEntryErrorMessages.push(`There was an error creating Trucking Rate: ${result[0]['ErrMsg']}`);
        } else {
          this.popupEntryErrorMessages.push(`There was an error editing Trucking Rate: ${result[0]['ErrMsg']}`);
        }
        return;
      }

      // Save Success
      this.viewMode = true;
      this.viewModePK = true;
      this.billRateInputValue = result[0]["Bill Rate Code"];
      this.popupEntryTitle = "View Trucking Rate";
      this.showTollRatesTableBox = false;
      this.$nextTick(() => {
        this.showTollRatesTableBox = true;
      });
      this.getBillRateListMaster();

      if (action == "N") {
        this.popupEntrySuccessMessages.push(`Successfully created Trucking Rate: ${result[0]["Bill Rate Code"]}`);
      } else {
        this.popupEntrySuccessMessages.push(`Successfully edited Trucking Rate: ${this.billRateInputValue}`);
      }
    },
    selectedFrom: async function (selected) {
      if (!selected) {
        return;
      }
      let areaCode = this.populateObject(this.areaListMaster, 'Area Code', selected['Area Code']);
      if (!this.selectedFromArea && selected?.['Area Code']) {
        this.selectedFromArea = areaCode;
        return;
      }

      // if area code is empty it will prompt to remove or keep 
      //else if area code is not found 
      if (selected['Area Code'] === '' && this.selectedFromArea) {
        const remainConfirmation = await this.$showAlert('warning', "New 'From Area' detected.", `Do you wish to keep 'From Area' as ${this.selectedFromArea['Area Code']}?`, [
          { text: 'Remove', textColor: 'white', color: 'red' },
          { text: 'Keep', textColor: 'black', color: 'lightgrey' },
        ]);
        if (remainConfirmation === "Remove") {
          this.selectedFromArea = null;
          return;
        } 
      } else if (selected['Area Code'] !== this.selectedFromArea['Area Code'] && areaCode) {
        const remainConfirmation = await this.$showAlert('warning', "New 'From Area' detected.", `Do you wish to replace 'From Area' with ${areaCode['Area Code']}?`, [
          { text: 'Replace'},
          { text: 'Keep', textColor: 'black', color: 'lightgrey' },
        ]);
        if (remainConfirmation === "Replace") {
          this.selectedFromArea = areaCode;
          return;
        }
      }
    },
    selectedTo: async function (selected) {
      if (!selected){
        return;
      }
      let areaCode = this.populateObject(this.areaListMaster, 'Area Code', selected['Area Code']);
      if ( !this.selectedFromArea && selected?.['Area Code']) {
        this.selectedToArea = areaCode;
        return;
      }

      if (selected['Area Code'] === '' && this.selectedToArea) {
        const remainConfirmation = await this.$showAlert('warning', "New 'To Area' detected.", `Do you wish to keep 'To Area' as ${this.selectedToArea['Area Code']}?`, [
          { text: 'Remove', textColor: 'white', color: 'red' },
          { text: 'Keep', textColor: 'black', color: 'lightgrey' },
        ]);
        if (remainConfirmation === "Remove") {
          this.selectedToArea = null;
          return;
        }
      } else if (selected['Area Code'] !== this.selectedToArea['Area Code'] && areaCode) {
        const remainConfirmation = await this.$showAlert('warning', "New 'From Area' detected.", `Do you wish to replace 'To Area' with ${areaCode['Area Code']}?`, [
          { text: 'Replace'},
          { text: 'Keep', textColor: 'black', color: 'lightgrey' },
        ]);
        if (remainConfirmation === "Replace") {
          this.selectedToArea = areaCode;
          return;
        }
      }
    },
    toFocusChanged: async function(focus) {
      if (!focus) {
        if(this.to1 != '') {
          this.from2 = (this.to1 == '0' || this.to1 == '0.0' || this.to1 == '0.00') ? '0.00' : (parseFloat(this.to1) + 1).toFixed(2);
        }
        if (this.to2 != '') {
          this.from3 = (this.to2 == '0' || this.to2 == '0.0' || this.to2 == '0.00') ? '0.00' : (parseFloat(this.to2) + 1).toFixed(2);
        } 
        if (this.to3 != '') {
          this.from4 = (this.to3 == '0' || this.to3 == '0.0' || this.to3 == '0.00') ? '0.00' : (parseFloat(this.to3) + 1).toFixed(2);
        }
      }
    },
    formatSaveBillRateRequestBody: function (action) {
      let requestBody = {
        'action': action,
        'systemcode': this.systemCodeIsChecked ? "1" : "0",
        'billratecode': this.billRateInputValue.toUpperCase().trim(),
        'originalstartvaliddate': this.billRateTableBoxSelectedRow[0]?.['Valid From'] ?? this.validFr.formatString('yyyy-MM-dd'),
        'startvaliddate': this.validFr.formatString('yyyy-MM-dd'),
        'description': this.description.toUpperCase().trim(),
        'departurecode': this.selectedFromDestination?.['Destination Code'] ?? "",
        'destinationcode': this.selectedToDestination?.['Destination Code'] ?? "",
        'departure': this.selectedFromArea?.["Area Code"] ?? "",
        'destination': this.selectedToArea?.["Area Code"] ?? "",
        'cu_number': this.selectedBillCust?.["Customer Code"] ?? "",
        'tonnerate': this.perKGRate,
        'm3rate': this.perM3Rate,
        'minimumrate': this.minimumRate,
        'toll': this.stdToll,
        'drvtonneratetype': this.selectedDrvPerKGRateType?.["label"] ?? "",
        'drvtonnerate': this.drvPerKGRate,
        'drvm3ratetype': this.selectedDrvPerM3RateType?.["label"] ?? "",
        'drvm3rate': this.drvPerM3Rate,
        'drvminimumrate': this.drvMinimumRate,
        'ind': 'N',
        'estkm': parseInt(this.estimatedKM),
        'transporter': '',
        'perpackagerate': this.perPackageRate,
        'percentcargovalrate': this.percentOfTtlProdVal,
        'pertruckrate': this.perTruckRate,
        'kgrate': this.perKGRate,
        'perpalletrate': this.perPalletRate,
        'adjustamt': '',
        'adjustamttype': '',
        'package_code': this.selectedPackageType?.["Package Code"] ?? "",
        'prodcode': this.selectedProductCode?.["Product Code"] ?? "",
        'prod_txnid': this.selectedProductCode?.['Product ID'] ?? "",
        'trucktype': this.selectedTruckType?.["Truck Type"] ?? "",
        'billrate_type': this.selectedRateBy?.["keyValue"] ?? "",
        'departurezone': this.selectedFromZone?.["Zone Type"] ?? "",
        'destinationzone': this.selectedToZone?.["Zone Type"] ?? "",
        'itemtype': this.selectedItemType?.["ItemType"] ?? "",
        'rangetype': 'R',
        'range1': this.to1,
        'range1rate': this.rate1,
        'range2': this.to2,
        'range2rate': this.rate2,
        'range3': this.to3,
        'range3rate': this.rate3,
        'range4': this.to4,
        'range4rate': this.rate4,
        'baseamt': this.baseAmount,
        'jobtype': this.selectedJobType?.["Job Type"] ?? "",
        'perjourneyrate': this.perJourneyRate,
        'droppointrate': this.droppointRate,
        'droppointthreshold': parseInt(this.ifNoOfDropsMoreThan),
        'billrategroup': '',
        'currency': this.selectedCurrency?.["Currency Code"] ?? "",
        'currencyrate': this.currencyRate,
        'atttonneratetype': this.selectedAttPerM3RateType?.["label"] ?? "",
        'atttonnerate': this.attPerKGRate,
        'attm3ratetype': this.selectedAttPerM3RateType?.["label"] ?? "",
        'attm3rate': this.attPerM3Rate,
        'attminimumrate': this.attMinimumRate,
      };

      if (action === "D") {
        return requestBody;
      }

      let tollRatesArr = [];
      for (const tollRatesData of this.tollRatesTableData) {
        if (!tollRatesData._action) {
          continue;
        }

        tollRatesArr.push({
          "action": tollRatesData._action,
          "trucktype": tollRatesData["Truck Type"],
          "toll": tollRatesData[["Toll Rate"]],
        })
      }
      requestBody.billratetoll = tollRatesArr;
      return requestBody;
    },
    populateTextTrim: function (text) { // To populate text with trim
      return text ? text.trim() : '';
    },
    populateObject: function (data, key, compare) { // To populate object into dropdown or input query
      try {
        return data.find(d => (d[key]?.trim?.() ?? d[key]) == (compare?.trim?.() ?? compare));
      } catch (error) {
        return null;
      }
    },
  },
  async mounted() {
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'Trucking Bill Rate') {
        x.data.forEach(k => {
          //key in menu same as control value from api
          let selectedMenu = this.menu.find(m => m['key'] == k['Control']);

          if (selectedMenu) {
            selectedMenu[k['Properties'].toLowerCase()] = (k['PropertiesValue'] == 'True');
          }
        })
      }
    });

    this.$fullscreenLoading('Getting Trucking Rate');
    await this.getBillRateListMaster();
    await this.getAreaListMasterListMaster();
    await this.getDestinationListMaster();
    await this.getBillCustListMaster();
    await this.getJobTypeListMaster();
    await this.getPackageListMaster();
    await this.getTruckTypeListMaster();
    await this.getProductCodeListMaster();
    await this.getCurrencyListMaster();
    await this.getZoneListMaster();
    await this.getItemTypeListMaster();
    this.$fullscreenLoading();
  },
  watch: {
  }
}
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.billRate-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;

  >.table-box-container {
    width: 100%;
    margin-left: 5px;
    padding: 5px;
    background-color: white;
    border-radius: 5px;
    display: flex;
    overflow: hidden;
  }

  .popup-entry-component {

    .general-inputs {
      display: flex;
      flex-direction: column;

      >.billRate-active-date {
        display: flex;
        flex-direction: row;

        >.billRate-input {
          margin-right: 3px;
        }

        >.systemcode-check-box {
          margin-left: 3px;
        }

        >.valid-from {
          margin-right: 3px;
          margin-top: 5px;
          display: flex;
          width: 25%;
        }

        >.radio-button-set-component {
          display: flex;
        }

        >.radio-button-set-component:nth-child(2) {
          margin-top: 5px;
        }
      }
    }

    .rate-by-area-destination {
      display: flex;
      flex-direction: column;
      overflow-y: auto;

      >.area-destination-inputs {
        display: flex;
        flex-direction: row;
        margin-top: 5px;

        >.input-column {
          display: flex;
          flex-direction: column;
          width: 50%;
          margin-right: 5px;

          >.sub-area-destination-inputs {
            display: flex;
            flex-direction: row;
            margin-top: 5px;

            >.sub-input-column {
              display: flex;
              flex-direction: column;
              margin: 5px 5px 0 0;
              width: 50%;
            }
          }

          .drv-rate {
            display: flex;
            flex-direction: column;

            >.sub-tab-rate-boxcontainer {

              >.sub-tab-rate-inputs {
                display: flex;
                flex-direction: row;
                margin-top: 5px;

                >.sub-tab-rate-input-column {
                  display: flex;
                  flex-direction: column;
                  margin: 5px 5px 0 0;
                  width: 50%;
                }
              }
            }
          }
        }

        >.toll-rates-list {
          display: flex;
          flex-direction: row;
          height: 100%;
          overflow-y: scroll;
          width: 100%;
          overflow-x: hidden;

          >.toll-rates-table {
            width: 100%;

            >.table-box-container {
              width: 100%;
              padding: 5px;
              background-color: white;
              border-radius: 5px;
              display: flex;
              overflow: hidden;
              height: 300px;
            }

            .call-to-action-buttons {
              display: flex;
              flex-direction: row;
              justify-content: flex-end;
              margin-right: 5px;

              >button:nth-child(1) {
                margin-right: 5px;
              }
            }
          }

          >.toll-rates-inputs {
            display: flex;
            flex-direction: column;
            height: 100%;
            width: 50%;
            margin-left: 3px;
          }
        }
      }
    }

    .rate-by-zone-product {
      display: flex;
      flex-direction: column;

      >.zone-product-inputs {
        display: flex;
        flex-direction: column;
        margin-top: 5px;

        >.input-row {
          display: flex;
          flex-direction: row;
          margin-right: 5px;
        }
      }
    }
  }
}
</style>